import { useState } from 'react';
import { parseDesignMd } from '@/lib/designTokenParser';
import { ColorTokenGroup } from './ColorTokenGroup';
import { TypographyTokenGroup } from './TypographyTokenGroup';
import { DimensionTokenGroup } from './DimensionTokenGroup';

interface VisualTokenEditorProps {
  markdown: string;
  onMarkdownChange: (md: string) => void;
}

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginTop: '24px',
  marginBottom: '12px',
  paddingBottom: '8px',
  borderBottom: '1px solid var(--color-border)',
};

const readOnlyBlockStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontFamily: 'monospace',
  fontSize: '11px',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  color: 'var(--text-secondary)',
  marginBottom: '6px',
};

export function VisualTokenEditor({ markdown, onMarkdownChange }: VisualTokenEditorProps) {
  const tokens = parseDesignMd(markdown);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (section: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => {
    const isCollapsed = collapsed.has(id);
    return (
      <div>
        <div
          style={{ ...sectionHeaderStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          onClick={() => toggle(id)}
        >
          <span>{title}</span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{isCollapsed ? '▶' : '▼'}</span>
        </div>
        {!isCollapsed && children}
      </div>
    );
  };

  const colorProps = { markdown, onMarkdownChange };

  return (
    <div>
      {/* Colors */}
      <Section id="colors" title="Colors">
        <ColorTokenGroup title="Brand Primitives" tokens={tokens.colors.brand} {...colorProps} />
        <ColorTokenGroup title="Semantic — Light Mode" tokens={tokens.colors.semanticLight} {...colorProps} />
        <ColorTokenGroup title="Semantic — Dark Mode" tokens={tokens.colors.semanticDark} {...colorProps} />
        <ColorTokenGroup title="Status Colors" tokens={tokens.colors.status} {...colorProps} />
        <ColorTokenGroup title="Role Accents" tokens={tokens.colors.roleAccents} {...colorProps} />
        <ColorTokenGroup title="Admin Card Accents" tokens={tokens.colors.adminCards} {...colorProps} />
      </Section>

      {/* Typography */}
      <Section id="typography" title="Typography">
        <TypographyTokenGroup title="Font Stacks" tokens={tokens.typography.fontStacks} previewType="fontFamily" {...colorProps} />
        <TypographyTokenGroup title="Type Scale" tokens={tokens.typography.typeScale} previewType="fontSize" {...colorProps} />
        <TypographyTokenGroup title="Font Weights" tokens={tokens.typography.fontWeights} previewType="fontWeight" {...colorProps} />
      </Section>

      {/* Spacing */}
      <Section id="spacing" title="Spacing">
        <DimensionTokenGroup title="Spacing Scale" tokens={tokens.spacing} previewType="spacing" {...colorProps} />
      </Section>

      {/* Border Radius */}
      <Section id="borderRadius" title="Border Radius">
        <DimensionTokenGroup title="Border Radius" tokens={tokens.borderRadius} previewType="borderRadius" {...colorProps} />
      </Section>

      {/* Shadows — read-only */}
      <Section id="shadows" title="Shadows">
        {tokens.shadows.length > 0 ? (
          tokens.shadows.map((t) => (
            <div key={t.lineIndex} style={readOnlyBlockStyle}>
              <strong>{t.name}:</strong> {t.value}
              {t.comment && <span style={{ color: 'var(--text-muted)' }}> — {t.comment}</span>}
            </div>
          ))
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No shadow tokens found</div>
        )}
      </Section>

      {/* Transitions — read-only */}
      <Section id="transitions" title="Transitions">
        {tokens.transitions.length > 0 ? (
          tokens.transitions.map((t) => (
            <div key={t.lineIndex} style={readOnlyBlockStyle}>
              <strong>{t.name}:</strong> {t.value}
              {t.comment && <span style={{ color: 'var(--text-muted)' }}> — {t.comment}</span>}
            </div>
          ))
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No transition tokens found</div>
        )}
      </Section>

      {/* Layout */}
      <Section id="layout" title="Layout">
        <DimensionTokenGroup title="Layout Values" tokens={tokens.layout} previewType="value" {...colorProps} />
      </Section>

      {/* Breakpoints */}
      <Section id="breakpoints" title="Breakpoints">
        <DimensionTokenGroup title="Breakpoints" tokens={tokens.breakpoints} previewType="value" {...colorProps} />
      </Section>

      {/* Gradients — read-only */}
      <Section id="gradients" title="Gradients">
        {tokens.gradients.length > 0 ? (
          tokens.gradients.map((t) => (
            <div key={t.lineIndex} style={readOnlyBlockStyle}>
              <strong>{t.name}:</strong> {t.value}
              {t.comment && <span style={{ color: 'var(--text-muted)' }}> — {t.comment}</span>}
            </div>
          ))
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No gradient tokens found</div>
        )}
      </Section>
    </div>
  );
}
